import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { ServerRootService } from '@se/se.ui.html5';
import { firstValueFrom, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { CLIENT_ID } from '../constants';
import { seExtensaAuth, seExtensaConnected } from '../root-store/root.actions';
import { RootState } from '../root-store/root.state';
import { ITokenResponse } from '../services/authentication/authentication.service';
import { RadioSettingsService } from '../services/settings/radio-settings.service';
import { selectExtensaAuth } from './connectivity.selectors';

/**
 * Diversa da ITokenResponse: access_token -> token
 */
interface IHostTokenResponse {
    token: string;
    token_type: string;
    expires_in: number;
    refresh_token: string;
}

@Injectable({
    providedIn: 'root',
})
export class ExtensaService extends ServerRootService {
    private baseUrl: string;
    private loginUrl: string;
    private signalrUrl: string;

    private auth$ = this.store.pipe(select(selectExtensaAuth));

    signalrHubConnection: SignalR.Hub.Connection;

    constructor(
        private http: HttpClient,
        private store: Store<RootState>,
        private radioSettingsService: RadioSettingsService,
    ) {
        super();
    }

    async init() {
        const settings = await firstValueFrom(this.radioSettingsService.radioSettingsAsync);
        const webApiBaseUrl = window['EstensaReviewUrl'] || `http://localhost:${settings.extensaSettings.webApiPort}`;

        this.baseUrl = `${webApiBaseUrl}/suitestensa/web/api`;
        this.loginUrl = `${webApiBaseUrl}/oauth2/login/estensa`;
        this.signalrUrl = `${webApiBaseUrl}/suitestensa`;

        this.signalrHubConnection = $.hubConnection(this.signalrUrl);
        this.signalrHubConnection.stateChanged((s) => this.onSignalrConnectionStateChanged(s));
    }

    async getAuthOptions() {
        const auth = await firstValueFrom(this.auth$);
        let headers = new HttpHeaders({
            'Content-Type': 'application/json',
        });

        if (auth) {
            headers = headers.set('Authorization', `Bearer ${auth.accessToken}`);
        }

        return { headers };
    }

    getBaseUrl() {
        return this.baseUrl;
    }

    async close() {
        return firstValueFrom(this.http.post<void>(`${this.baseUrl}/Host/Close`, {}, this.buildOptions()));
    }

    async login() {
        const pairingToken = await firstValueFrom(
            this.http.post<IHostTokenResponse>(`${this.getServerRoot()}/Authentication/GetUserPairingToken`, {}),
        );

        const authTokenRequestbody = new HttpParams()
            .set('pairingToken', pairingToken.token)
            .set('grant_type', 'client_credentials')
            .set('client_id', CLIENT_ID);

        const { access_token, expires_in, refresh_token } = await firstValueFrom(
            this.http.post<ITokenResponse>(this.loginUrl, authTokenRequestbody),
        );

        this.store.dispatch(
            seExtensaAuth({
                accessToken: access_token,
                expiresIn: expires_in,
                refreshToken: refresh_token,
            }),
        );

        return this.startConnection(access_token);
    }

    async startConnection(accessToken: string) {
        this.signalrHubConnection.qs = { Bearer: accessToken };
        if (!accessToken) {
            return;
        }

        this.signalrHubConnection.stop();
        // Promise di jQuery a vera promise.
        return new Promise((res, rej) => {
            this.signalrHubConnection.start().then(res).fail(rej);
        });
    }

    async logout() {
        return firstValueFrom(this.http.post<void>(`${this.baseUrl}/Host/Logout`, {}, this.buildOptions()));
    }

    async minimize() {
        return firstValueFrom(this.http.post<void>(`${this.baseUrl}/Host/Minimize`, {}, this.buildOptions()));
    }

    async ping() {
        return firstValueFrom(
            this.http.post(`${this.baseUrl}/Host/Ping`, {}).pipe(
                map(() => true),
                catchError(() => of(false)),
            ),
        );
    }

    async isAvailable() {
        return this.ping();
    }

    async waitUntilAvailable() {
        const sleep = () => new Promise((res) => window.setTimeout(res, 5_000));
        const timeoutTime = Date.now() + 30_000;

        while (true) {
            if (await this.ping()) {
                return true;
            }

            if (Date.now() > timeoutTime) {
                return false;
            }
            await sleep();
        }
    }

    private onSignalrConnectionStateChanged({ newState }) {
        if (newState === SignalR.ConnectionState.Connected) {
            this.store.dispatch(seExtensaConnected({ isConnected: true }));
        } else if (newState === SignalR.ConnectionState.Disconnected) {
            this.store.dispatch(seExtensaConnected({ isConnected: false }));
        }
    }
}
